import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Package, Users, Star, Award, Shield, Clock } from 'lucide-react';
import { MissionStatCard, ScrollReveal } from './TacticalAnimations';
import { TechLabel } from '../common/TacticalCard';
import { VideoBackground } from './VideoBackground';

const iconMap = {
  Package,
  Users,
  Star,
  Award,
  Shield,
  Clock
};

const MissionSection = ({
  title = 'FIELD READY',
  description = 'VAV Wear, taktik ve outdoor giyim sektöründe öncü bir marka olarak faaliyet göstermektedir.',
  secondaryDescription = 'Her ürün, askeri standartlarda test edilmiş malzemelerle üretilir.',
  imageUrl = '',
  videoUrl = '',
  stats = []
}) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);

  // Default stats
  const defaultStats = [
    { value: '50+', label: 'Ürün', icon: 'Package' },
    { value: '10K+', label: 'Müşteri', icon: 'Users' },
    { value: '4.9', label: 'Puan', icon: 'Star' },
  ];

  const displayStats = stats.length > 0 ? stats : defaultStats;

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 bg-vav-bg-paper overflow-hidden" 
      data-testid="mission-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 noise-texture" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image/Video Side */}
          <ScrollReveal direction="left">
            <motion.div 
              className="relative"
              style={{ y: imageY }}
            >
              {/* Main Image/Video Container */}
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl">
                {videoUrl ? (
                  <VideoBackground
                    videoUrl={videoUrl}
                    fallbackImage={imageUrl || 'https://images.unsplash.com/photo-1683642765591-2370edc15193?w=800&q=80'}
                    overlay={false}
                    showControls={true}
                  />
                ) : (
                  <motion.img
                    src={imageUrl || 'https://images.unsplash.com/photo-1683642765591-2370edc15193?w=800&q=80'}
                    alt="Mission"
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }}
                  />
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-vav-bg-paper/50 to-transparent pointer-events-none" />
                
                {/* Tactical corner brackets */}
                <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-vav-gold/50" />
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-vav-gold/50" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-vav-gold/50" />
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-vav-gold/50" />
              </div>

              {/* Floating Label */}
              <motion.div 
                className="absolute -bottom-4 -right-4 bg-vav-bg p-4 rounded-xl border border-vav-border shadow-lg"
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.5, type: "spring" }}
              >
                <TechLabel variant="gold">[VAV-SPEC]</TechLabel>
              </motion.div>

              {/* Decorative elements */}
              <motion.div 
                className="absolute -top-6 -left-6 w-24 h-24 border border-vav-gold/20 rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
            </motion.div>
          </ScrollReveal>

          {/* Content Side */}
          <motion.div style={{ y: textY }}>
            <ScrollReveal direction="right">
              <TechLabel variant="accent" className="mb-4">[MİSYON]</TechLabel>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={0.1}>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-vav-fg mb-6">
                {title}
              </h2>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={0.2}>
              <p className="text-vav-fg-muted leading-relaxed mb-6 text-lg">
                {description}
              </p>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={0.3}>
              <p className="text-vav-fg-muted leading-relaxed mb-8">
                {secondaryDescription}
              </p>
            </ScrollReveal>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              {displayStats.map((stat, index) => {
                const Icon = iconMap[stat.icon] || Package;
                return (
                  <MissionStatCard
                    key={index}
                    value={stat.value}
                    label={stat.label}
                    icon={Icon}
                    delay={0.4 + index * 0.1}
                  />
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative line */}
      <motion.div 
        className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vav-gold/30 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.5, delay: 0.5 }}
      />
    </section>
  );
};

export default MissionSection;
